import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';

import { useQueryGetMatrixData } from 'api/queries/getMatrixData.query';
import { useQueryPrioritiesMatrixCards } from 'api/queries/prioritiesMatrixCards.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { ITypeAndRole } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

const MATRIX_PROGRAM_ACTIVITIES_DEFAULT_ID = 3;

export const usePrioritiesMatrixComponentProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    endpoints,
}) => {
    const [matrixCardCategoryId, setMatrixCardCategoryId] = useState<number>(MATRIX_PROGRAM_ACTIVITIES_DEFAULT_ID);
    const [matrixFilterUrlParam, setMatrixFilterUrlParam] = useQueryState('matrixFilter');
    const [isOverdueFilter, setIsOverdueFilter] = useState<any>(false);
    const [isDelayDriverFilter, setIsDelayDriverFilter] = useState<any>(false);
    const [isAssignedToMeFilter, setIsAssignedToMeFilter] = useState<any>(false);
    const [isCriticalPathFilter, setIsCriticalPathFilter] = useState<any>(false);
    const [selectedTypeAndRole, setSelectedTypeAndRole] = useState<ITypeAndRole>({
        type: 'doNowComponent',
        role: 'itemProgramActivities',
    });

    const { id: versionId } = useParams<ProgramStatusUrlParams>();

    const source = widgetId
        ? localFilters
        : {
              ...globalFilterData,
              numOfDaysToBeUrgent: Number(matrixFilterUrlParam),
              isOverdueFilter,
              isDelayDriverFilter,
              isAssignedToMeFilter,
              isCriticalPathFilter,
              selectedTypeAndRole,
              versionId,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs],
    );

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
        },
        globalFilter: {
            tags: source?.tags,
            wbs: source?.wbs,
            milestoneFragnet: source?.milestoneFragnet,
            activityCodes: source?.activityCodes,
        },
        local: {
            numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
            isOverdueFilter: source.isOverdueFilter,
            isDelayDriverFilter: source.isDelayDriverFilter,
            isAssignedToMeFilter: source.isAssignedToMeFilter,
            isCriticalPathFilter: source.isCriticalPathFilter,
            selectedTypeAndRole: source.selectedTypeAndRole,
            versionId: source.versionId,
        },
    };

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };
    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    //queries
    const matrixDataPayload = {
        metaDataId: latestVersionId,
        numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
        filter: {
            filters,
        },
    };

    const prioritiesMatrixCardPayload = {
        versionId: latestVersionId,
        numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
        filter: {
            filters,
        },
        itemId: matrixCardCategoryId,
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilter,
                ...state.local,
            },
            setters: {
                setMatrixCardCategoryId,
                setMatrixFilterUrlParam,
                setIsOverdueFilter,
                setIsDelayDriverFilter,
                setIsAssignedToMeFilter,
                setIsCriticalPathFilter,
                setSelectedTypeAndRole,
            },
            pills,
            flatPills: { ...pillsData },
        },
        api: {
            matrixData: {
                instance: useQueryGetMatrixData(matrixDataPayload, endpoints.getCardMatrixCountersData),
                payload: matrixDataPayload,
                endpoint: endpoints.getCardMatrixCountersData,
            },
            cards: useQueryPrioritiesMatrixCards(prioritiesMatrixCardPayload),
        },
    };
};
